import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Card, Skeleton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import MerchantDetailCard from "./components/MerchantDetailCard";
import useApi from "../../components/Utils/api";

const describeError = (err) => {
  if (!err) return "Something went wrong. Please try again.";
  if (err.status === 0) {
    return "Could not reach the middleware API. Check that the service is running and reachable.";
  }
  return err.data?.message || err.data?.error || err.message || "Something went wrong.";
};

export default function MerchantDetail() {
  const { state } = useLocation();
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();

  const [merchant, setMerchant] = useState(state?.merchant || null);
  const [total, setTotal] = useState(state?.total);
  const [loading, setLoading] = useState(!state?.merchant);
  const [error, setError] = useState("");

  // There is no single-merchant endpoint on the middleware, so a direct visit or a
  // refresh falls back to fetching the list and selecting this row from it.
  const fetchMerchant = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("merchant/allMerchant");
      const list = Array.isArray(res?.data?.merchants) ? res.data.merchants : [];
      setMerchant(list.find((m) => m.internalMerchantId === id) || null);
      setTotal(Number.isInteger(res?.data?.total) ? res.data.total : list.length);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => {
    if (state?.merchant) return;
    fetchMerchant();
  }, [fetchMerchant, state]);

  const backButton = (
    <MDButton
      variant="text"
      color="info"
      onClick={() => navigate("/dashboard")}
      startIcon={<ArrowBackIcon />}
      sx={{ textTransform: "none", fontWeight: 600, ml: -1 }}
    >
      All merchants
    </MDButton>
  );

  let body;
  if (loading) {
    body = (
      <Card>
        <MDBox px={3} py={3}>
          <Skeleton variant="text" width="40%" height={36} />
          <Skeleton variant="text" width="25%" height={20} />
          <MDBox mt={3} display="flex" flexDirection="column" gap={1.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={`s-${i}`} variant="text" width={`${80 - i * 6}%`} height={22} />
            ))}
          </MDBox>
        </MDBox>
      </Card>
    );
  } else if (error) {
    body = (
      <Card>
        <MDBox px={3} py={6} display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Alert severity="error" sx={{ maxWidth: 560 }}>
            {error}
          </Alert>
          <MDButton
            variant="gradient"
            color="info"
            onClick={fetchMerchant}
            startIcon={<RefreshIcon />}
          >
            Try again
          </MDButton>
        </MDBox>
      </Card>
    );
  } else if (!merchant) {
    body = (
      <Card>
        <MDBox px={3} py={6} display="flex" flexDirection="column" alignItems="center" gap={1}>
          <MDTypography variant="h6" color="dark" fontWeight="medium">
            Merchant not found
          </MDTypography>
          <MDTypography
            variant="button"
            color="text"
            sx={{ textTransform: "none", textAlign: "center" }}
          >
            No merchant is registered with the internal ID “{id}”.
          </MDTypography>
          <MDBox mt={2}>
            <MDButton variant="gradient" color="info" onClick={() => navigate("/dashboard")}>
              Back to merchants
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    );
  } else {
    body = <MerchantDetailCard merchant={merchant} total={total} />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={1}>{backButton}</MDBox>
        {body}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
